import { AlertCircle } from 'lucide-react';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';
import { etiquetaOrigen, fechaUnidad } from '@/features/inventario/stockUnidades';
import type { PropuestaUnidades } from '../api';

/** unidadId → cantidad que sale de esa unidad (sólo las > 0). */
export type SeleccionUnidades = Record<number, number>;

const num = (n: number) => String(Math.round(n * 1000) / 1000).replace('.', ',');

export const totalSeleccion = (s: SeleccionUnidades | undefined) =>
  Math.round(Object.values(s ?? {}).reduce((t, n) => t + n, 0) * 1000) / 1000;

/** La selección que el servidor sugirió (más antiguas primero) como mapa. */
export const seleccionSugerida = (p: PropuestaUnidades): SeleccionUnidades =>
  Object.fromEntries(p.sugeridas.map((s) => [s.unidadId, s.cantidad]));

/**
 * Elegir de qué unidades de stock sale una línea del despacho. Cada fila es una
 * unidad (con serie, un horno) o un ingreso (sin serie, con su saldo), con su
 * compra / importación de origen y la fecha de llegada. Viene preseleccionada la
 * más antigua (o la que tiene la serie ya cargada); la persona puede cambiarla.
 */
export function UnidadesOrigenPicker({
  propuesta,
  cantidad,
  seleccion,
  onChange,
}: {
  propuesta: PropuestaUnidades;
  cantidad: number;
  seleccion: SeleccionUnidades;
  onChange: (next: SeleccionUnidades) => void;
}) {
  const elegidas = totalSeleccion(seleccion);
  const falta = Math.round((cantidad - elegidas) * 1000) / 1000;

  const set = (unidadId: number, valor: number) => {
    const next = { ...seleccion };
    if (valor > 0) next[unidadId] = valor;
    else delete next[unidadId];
    onChange(next);
  };

  return (
    <div className="mt-3 space-y-1.5 rounded-md border bg-muted/20 p-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Origen del stock
        </p>
        <p className="text-[11px] text-muted-foreground">
          {propuesta.porSerie
            ? 'Elegida por el Nº de serie de la línea.'
            : 'Por defecto, las más antiguas.'}
        </p>
      </div>

      {propuesta.unidades.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No hay unidades cargadas de este artículo: se descuenta del stock sin indicar el origen.
        </p>
      ) : (
        <ul className="divide-y rounded-md border bg-background">
          {propuesta.unidades.map((u) => {
            const valor = seleccion[u.id] ?? 0;
            // Una unidad con serie es un solo equipo: se tilda; el resto lleva cantidad.
            const unitaria = propuesta.serializado || u.nroSerie != null || u.cantidadDisponible <= 1;
            const alcanzo = elegidas >= cantidad - 0.0001;
            return (
              <li key={u.id} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                {unitaria ? (
                  <Checkbox
                    checked={valor > 0}
                    disabled={valor === 0 && alcanzo}
                    onCheckedChange={(c) => set(u.id, c === true ? Math.min(1, u.cantidadDisponible) : 0)}
                    aria-label={`Tomar la unidad ${u.nroSerie ?? u.id}`}
                  />
                ) : (
                  <Input
                    type="number"
                    min={0}
                    max={u.cantidadDisponible}
                    step="any"
                    inputMode="decimal"
                    value={valor || ''}
                    placeholder="0"
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      set(u.id, Number.isFinite(n) ? Math.min(Math.max(n, 0), u.cantidadDisponible) : 0);
                    }}
                    className="h-7 w-16 px-1.5 text-right text-xs"
                    aria-label={`Cantidad a tomar de ${etiquetaOrigen(u)}`}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{etiquetaOrigen(u)}</span>
                  <span className="text-muted-foreground">
                    {' · '}llegó {fechaUnidad(u.fechaIngreso)}
                    {u.nroSerie ? ` · serie ${u.nroSerie}` : ''}
                    {u.nroRemito ? ` · remito ${u.nroRemito}` : ''}
                  </span>
                </div>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {unitaria ? (u.cantidadDisponible > 1 ? `quedan ${num(u.cantidadDisponible)}` : '1 un.') : `quedan ${num(u.cantidadDisponible)}`}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {propuesta.unidades.length > 0 && (
        <p
          className={cn(
            'flex items-center gap-1 text-[11px]',
            falta < -0.0001 ? 'text-destructive' : falta > 0.0001 ? 'text-amber-700' : 'text-muted-foreground',
          )}
        >
          {(falta < -0.0001 || falta > 0.0001) && <AlertCircle className="h-3 w-3 shrink-0" aria-hidden />}
          {falta < -0.0001
            ? `Elegiste ${num(elegidas)} y la línea es de ${num(cantidad)}.`
            : falta > 0.0001
              ? `Elegidas ${num(elegidas)} de ${num(cantidad)}: ${num(falta)} sin origen asignado (se descuentan igual del stock).`
              : `Elegidas ${num(elegidas)} de ${num(cantidad)}.`}
        </p>
      )}
    </div>
  );
}
